import {
  DEMO_PARENT_EMAIL,
  staff,
  staffEmail,
  type StaffMember,
  type Student,
} from "./data";

/** Prototype-only localStorage auth when Supabase is not configured. */
export const DEMO_PASSWORD = "lift";

/** Shared password for QA dev shortcut accounts (see scripts/seed-qa-dev-auth.mjs). */
export const LOCAL_DEV_PASSWORD = "lift1234";
export const AUTH_STORAGE_KEY = "lift-app-auth-v2";

export type AuthRole = "student" | "staff" | "parent";

export type AuthAccount = {
  email: string;
  password: string;
  role: AuthRole;
  given: string;
  family: string;
  /** Parent / guardian mobile; stored at signup or in Supabase user metadata. */
  phone: string;
  studentId: string | null;
  studentIds: string[];
  staffId: string | null;
  /**
   * Migration sign-in. The temporary password only opens the claim screen.
   * The rest of Lift stays closed until this is cleared.
   */
  mustResetPassword: boolean;
  /** Children assigned from the previous system and not yet accepted. */
  pendingStudentIds: string[];
  /** Present after this browser creates the account. Hosted logins keep dates in Supabase. */
  createdAt?: string;
  /** Present after a successful sign-in on this browser. */
  lastSignInAt?: string;
};

export type AuthSession = {
  email: string;
  role: AuthRole;
};

export type PasswordReset = {
  token: string;
  email: string;
};

export type AuthState = {
  accounts: AuthAccount[];
  session: AuthSession | null;
  resets: PasswordReset[];
};

export type AuthOk = { ok: true; state: AuthState };
export type AuthFail = { ok: false; error: string; state: AuthState };
export type AuthAttempt = AuthOk | AuthFail;
export type ResetAttempt =
  | { ok: true; state: AuthState; token: string }
  | { ok: false; error: string; state: AuthState };

export type SignUpInput = {
  given: string;
  family: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
};

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function accountForStudent(student: Student): AuthAccount {
  return {
    email: normalizeEmail(student.email),
    password: DEMO_PASSWORD,
    role: "student",
    given: student.given,
    family: student.family,
    phone: "",
    studentId: student.id,
    studentIds: [student.id],
    staffId: null,
    mustResetPassword: false,
    pendingStudentIds: [],
  };
}

export function accountForStaff(member: StaffMember = staff): AuthAccount {
  return {
    email: staffEmail(member),
    password: DEMO_PASSWORD,
    role: "staff",
    given: member.given,
    family: member.family,
    phone: "",
    studentId: null,
    studentIds: [],
    staffId: member.id,
    mustResetPassword: false,
    pendingStudentIds: [],
  };
}

export function accountForParent(
  given: string,
  family: string,
  email: string,
  studentIds: string[] = [],
  phone = "",
): AuthAccount {
  return {
    email: normalizeEmail(email),
    password: DEMO_PASSWORD,
    role: "parent",
    given,
    family,
    phone: phone.trim(),
    studentId: null,
    studentIds,
    staffId: null,
    mustResetPassword: false,
    pendingStudentIds: [],
  };
}

export function normalizeAccount(account: AuthAccount): AuthAccount {
  const studentIds =
    account.studentIds ?? (account.studentId ? [account.studentId] : []);
  return {
    ...account,
    phone: account.phone?.trim() ?? "",
    studentIds,
    mustResetPassword: account.mustResetPassword === true,
    pendingStudentIds: Array.isArray(account.pendingStudentIds)
      ? account.pendingStudentIds
      : [],
  };
}

/** A migrated parent cannot open Lift until the password is replaced and every assigned child is accepted or declined. */
export function parentClaimOpen(
  account: Pick<AuthAccount, "role" | "mustResetPassword" | "pendingStudentIds">,
): boolean {
  return (
    account.role === "parent" &&
    (account.mustResetPassword || account.pendingStudentIds.length > 0)
  );
}

/** Staff can edit any student. A parent can edit a student already on their account. */
export function canEditStudent(account: AuthAccount | null, studentId: string): boolean {
  if (!account || !studentId) return false;
  if (account.role === "staff") return true;
  return account.role === "parent" && studentIdsForAccount(account).includes(studentId);
}

export function studentIdsForAccount(account: AuthAccount): string[] {
  if (account.role === "parent") return account.studentIds;
  if (account.studentId) return [account.studentId];
  return account.studentIds;
}

function seedParentAccounts(students: Student[]): AuthAccount[] {
  const byGuardian = new Map<string, Student[]>();
  students.forEach((student) => {
    const email = normalizeEmail(student.guardianEmail);
    const list = byGuardian.get(email) ?? [];
    list.push(student);
    byGuardian.set(email, list);
  });
  return [...byGuardian.entries()]
    .filter(([, kids]) => kids.length > 1)
    .map(([email, kids]) => {
      const [first] = kids;
      const [given] = first.guardianName.split(" ");
      return accountForParent(
        given || first.guardianName,
        first.family,
        email,
        kids.map((kid) => kid.id),
      );
    });
}

export function seedAccounts(
  students: Student[],
  member: StaffMember = staff,
): AuthAccount[] {
  const parents = seedParentAccounts(students);
  if (!parents.some((account) => account.email === DEMO_PARENT_EMAIL)) {
    const kolenoKids = students.filter(
      (student) => normalizeEmail(student.guardianEmail) === DEMO_PARENT_EMAIL,
    );
    parents.push(
      accountForParent(
        "Julia",
        "Koleno",
        DEMO_PARENT_EMAIL,
        kolenoKids.map((student) => student.id),
        "(773) 555-7448",
      ),
    );
  }
  return [
    ...students.map(accountForStudent),
    accountForStaff(member),
    ...parents,
  ];
}

export function emptyAuthState(
  students?: Student[],
  member: StaffMember = staff,
): AuthState {
  void students;
  void member;
  return {
    accounts: [],
    session: null,
    resets: [],
  };
}

/** Add seeded demo accounts, or remove untouched ones when demos are off. */
export function applyDemoAccounts(
  state: AuthState,
  students: Student[],
  enabled: boolean,
  member: StaffMember = staff,
): AuthState {
  if (enabled) return mergeSeedAccounts(state, students, member);
  const seeded = new Set(
    seedAccounts(students, member).map((account) => account.email),
  );
  const accounts = state.accounts.filter(
    (account) =>
      !(seeded.has(account.email) && account.password === DEMO_PASSWORD),
  );
  const session =
    state.session &&
    accounts.some((account) => account.email === state.session?.email)
      ? state.session
      : null;
  if (
    accounts.length === state.accounts.length &&
    session === state.session
  ) {
    return state;
  }
  return { ...state, accounts, session };
}

export function mergeSeedAccounts(
  state: AuthState,
  students: Student[],
  member: StaffMember = staff,
): AuthState {
  const emails = new Set(state.accounts.map((account) => account.email));
  const extras = seedAccounts(students, member).filter(
    (account) => !emails.has(account.email),
  );
  const accounts = state.accounts.map(normalizeAccount);
  if (extras.length === 0) {
    return accounts === state.accounts ? state : { ...state, accounts };
  }
  return { ...state, accounts: [...accounts, ...extras] };
}

export function findAccount(
  state: AuthState,
  email: string,
): AuthAccount | undefined {
  const key = normalizeEmail(email);
  return state.accounts.find((account) => account.email === key);
}

export function passwordError(password: string): string | undefined {
  if (password.trim().length < 4) {
    return "Password needs at least 4 characters.";
  }
  return undefined;
}

function withSession(state: AuthState, account: AuthAccount): AuthState {
  return {
    ...state,
    session: { email: account.email, role: account.role },
    resets: state.resets.filter((reset) => reset.email !== account.email),
  };
}

export const DUPLICATE_EMAIL_MESSAGE =
  "That email already has an account. Sign in or reset the password.";

export function signIn(
  state: AuthState,
  email: string,
  password: string,
): AuthAttempt {
  const typed = password.trim();
  if (!normalizeEmail(email) || !typed) {
    return { ok: false, error: "Enter an email and password.", state };
  }
  const account = findAccount(state, email);
  if (!account) {
    return {
      ok: false,
      error: "No account for that email.",
      state,
    };
  }
  if (account.password !== typed) {
    return {
      ok: false,
      error: "Password does not match.",
      state,
    };
  }
  const seen: AuthAccount = { ...account, lastSignInAt: new Date().toISOString() };
  return {
    ok: true,
    state: withSession(
      {
        ...state,
        accounts: state.accounts.map((item) =>
          item.email === seen.email ? seen : item,
        ),
      },
      seen,
    ),
  };
}

export function signUp(state: AuthState, input: SignUpInput): AuthAttempt {
  const given = input.given.trim();
  const family = input.family.trim();
  const email = normalizeEmail(input.email);
  if (!given || !family || !email) {
    return {
      ok: false,
      error: "Fill in name and email.",
      state,
    };
  }
  if (!input.phone.trim()) {
    return { ok: false, error: "Add a mobile number for guardian contact.", state };
  }
  const lengthError = passwordError(input.password);
  if (lengthError) return { ok: false, error: lengthError, state };
  if (input.password !== input.confirmPassword) {
    return { ok: false, error: "Passwords do not match.", state };
  }
  if (findAccount(state, email)) {
    return {
      ok: false,
      error: DUPLICATE_EMAIL_MESSAGE,
      state,
    };
  }
  const now = new Date().toISOString();
  const account: AuthAccount = {
    email,
    password: input.password,
    role: "parent",
    given,
    family,
    phone: input.phone.trim(),
    studentId: null,
    studentIds: [],
    staffId: null,
    mustResetPassword: false,
    pendingStudentIds: [],
    createdAt: now,
    lastSignInAt: now,
  };
  return {
    ok: true,
    state: withSession(
      { ...state, accounts: [account, ...state.accounts] },
      account,
    ),
  };
}

/** Opens a prototype parent session after Supabase has already signed them in. */
export function rememberSignedInParent(
  state: AuthState,
  input: { given: string; family: string; email: string },
): AuthAttempt {
  const given = input.given.trim();
  const family = input.family.trim();
  const email = normalizeEmail(input.email);
  if (!given || !family || !email) {
    return { ok: false, error: "Fill in name and email.", state };
  }
  const existing = findAccount(state, email);
  if (existing && existing.role !== "parent") {
    return {
      ok: false,
      error: "That email already has an account.",
      state,
    };
  }
  const account: AuthAccount = {
    email,
    password: existing?.password ?? "",
    role: "parent",
    given,
    family,
    phone: existing?.phone ?? "",
    studentId: null,
    studentIds: existing?.studentIds ?? [],
    staffId: null,
    mustResetPassword: existing?.mustResetPassword === true,
    pendingStudentIds: existing?.pendingStudentIds ?? [],
  };
  const accounts = existing
    ? state.accounts.map((item) => (item.email === email ? account : item))
    : [account, ...state.accounts];
  return {
    ok: true,
    state: withSession({ ...state, accounts }, account),
  };
}

export function signOut(state: AuthState): AuthState {
  if (!state.session) return state;
  return { ...state, session: null };
}

export function requestPasswordReset(
  state: AuthState,
  email: string,
  token = `reset-${Math.random().toString(36).slice(2, 10)}`,
): ResetAttempt {
  if (!normalizeEmail(email)) {
    return { ok: false, error: "Enter the email on your account.", state };
  }
  const account = findAccount(state, email);
  if (!account) {
    return {
      ok: false,
      error: "No account for that email.",
      state,
    };
  }
  const reset: PasswordReset = { token, email: account.email };
  return {
    ok: true,
    token,
    state: {
      ...state,
      resets: [
        reset,
        ...state.resets.filter((item) => item.email !== account.email),
      ],
    },
  };
}

export function resetPassword(
  state: AuthState,
  token: string,
  password: string,
  confirmPassword: string,
): AuthAttempt {
  const reset = state.resets.find((item) => item.token === token);
  if (!reset) {
    return {
      ok: false,
      error: "This reset link expired. Request a new one.",
      state,
    };
  }
  const lengthError = passwordError(password);
  if (lengthError) return { ok: false, error: lengthError, state };
  if (password !== confirmPassword) {
    return { ok: false, error: "Passwords do not match.", state };
  }
  return {
    ok: true,
    state: {
      ...state,
      session: null,
      accounts: state.accounts.map((account) =>
        account.email === reset.email ? { ...account, password } : account,
      ),
      resets: state.resets.filter((item) => item.token !== token),
    },
  };
}

export function ensureStudentAccount(
  state: AuthState,
  student: Student,
): AuthState {
  const email = normalizeEmail(student.email);
  if (!email) return state;
  const byId = state.accounts.find(
    (account) => account.studentId === student.id && account.role === "student",
  );
  if (byId) {
    const taken = findAccount(state, email);
    const nextEmail = !taken || taken.email === byId.email ? email : byId.email;
    if (
      byId.email === nextEmail &&
      byId.given === student.given &&
      byId.family === student.family
    ) {
      return state;
    }
    const next: AuthAccount = {
      ...byId,
      email: nextEmail,
      given: student.given,
      family: student.family,
      studentIds: [student.id],
    };
    return {
      ...state,
      accounts: state.accounts.map((account) =>
        account.email === byId.email ? next : account,
      ),
      session:
        state.session?.email === byId.email
          ? { email: nextEmail, role: "student" }
          : state.session,
    };
  }
  const existing = findAccount(state, email);
  if (existing) {
    if (existing.role === "parent" || existing.role === "staff") {
      return state;
    }
    if (
      existing.studentId === student.id &&
      existing.given === student.given &&
      existing.family === student.family &&
      existing.role === "student"
    ) {
      return state;
    }
    const next: AuthAccount = {
      ...existing,
      role: "student",
      given: student.given,
      family: student.family,
      studentId: student.id,
      studentIds: [student.id],
    };
    return {
      ...state,
      accounts: state.accounts.map((account) =>
        account.email === email ? next : account,
      ),
      session:
        state.session?.email === email
          ? { email, role: "student" }
          : state.session,
    };
  }
  return {
    ...state,
    accounts: [...state.accounts, accountForStudent(student)],
  };
}

export function ensureStudentAccounts(
  state: AuthState,
  students: Student[],
): AuthState {
  return students.reduce(
    (next, student) => ensureStudentAccount(next, student),
    state,
  );
}

export function attachStudentToParent(
  state: AuthState,
  parentEmail: string,
  studentId: string,
): AuthState {
  const parent = findAccount(state, parentEmail);
  if (!parent || parent.role !== "parent") return state;
  if (parent.studentIds.includes(studentId)) return state;
  const next: AuthAccount = {
    ...parent,
    studentIds: [...parent.studentIds, studentId],
  };
  return {
    ...state,
    accounts: state.accounts.map((account) =>
      account.email === parent.email ? next : account,
    ),
  };
}

export function destinationForAccount(account: AuthAccount): string {
  if (account.role === "staff") return "/staff";
  if (account.role === "parent") {
    if (parentClaimOpen(account)) return "/claim";
    return account.studentIds.length > 0 ? "/family" : "/register";
  }
  if (account.studentId) return `/student/${account.studentId}`;
  return "/register";
}

export function safeNextPath(next: string | null): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}
